import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import {
  LayoutDashboard, FileText, PlusCircle, Send, User,
  Bell, LogOut, ChevronLeft, ChevronRight,
  CheckCircle, XCircle, Sparkles,
} from 'lucide-react';

import { isTokenExpired, scheduleAutoLogout } from '../../utils/auth';
import IvisoraLogo from '../../components/shared/IvisoraLogo';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const NAV_GROUPS = [
  {
    label: null,
    items: [
      { label: 'Dashboard', path: '/designer/dashboard', icon: LayoutDashboard },
    ],
  },
  {
    label: 'Templates',
    items: [
      { label: 'My Templates',    path: '/designer/my-templates',  icon: FileText },
      { label: 'Create Template', path: '/designer/create',         icon: PlusCircle, action: true },
      { label: 'Submissions',     path: '/designer/submissions',   icon: Send },
      { label: 'AI Templates',    path: '/designer/ai-templates',  icon: Sparkles },
    ],
  },
  {
    label: 'Account',
    items: [
      { label: 'Notifications', path: '/designer/notifications', icon: Bell, showBadge: true },
      { label: 'Profile',       path: '/designer/profile',       icon: User },
    ],
  },
];

const DesignerLayout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [user, setUser] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [notifOpen, setNotifOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const autoLogoutRef = useRef(null);

  // Cancel any pending auto-logout timer on unmount
  useEffect(() => () => { if (autoLogoutRef.current) clearTimeout(autoLogoutRef.current); }, []);

  useEffect(() => {
    const token = localStorage.getItem('user_token');
    if (!token) { navigate('/designer/login'); return; }

    // ── Fast local expiry check (no network round-trip) ───────────────────────
    if (isTokenExpired(token)) {
      localStorage.removeItem('user_token');
      localStorage.removeItem('user');
      navigate('/designer/login');
      return;
    }

    try { setUser(JSON.parse(localStorage.getItem('user') || '{}')); } catch {}

    // Schedule automatic logout when this token's exp is reached
    if (autoLogoutRef.current) clearTimeout(autoLogoutRef.current);
    autoLogoutRef.current = scheduleAutoLogout(
      token,
      ['user_token', 'user'],
      () => navigate('/designer/login'),
    );
  }, [navigate]);

  useEffect(() => {
    const token = localStorage.getItem('user_token');
    if (!token || !API_URL) return;
    fetch(`${API_URL}/api/designer/notifications`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : [])
      .then(data => {
        const arr = Array.isArray(data) ? data : [];
        setNotifications(arr);
        setUnreadCount(arr.filter(n => !n.is_read).length);
      })
      .catch(() => {});
  }, [location.pathname]);

  const handleLogout = () => {
    if (autoLogoutRef.current) clearTimeout(autoLogoutRef.current);
    localStorage.removeItem('user_token');
    localStorage.removeItem('user');
    navigate('/designer/login');
  };

  const isActive = (path) =>
    location.pathname === path || location.pathname.startsWith(path + '/');

  const currentLabel = NAV_GROUPS.flatMap(g => g.items).find(n => isActive(n.path))?.label || 'Designer Portal';

  return (
    <div className="min-h-screen bg-[#f5f6fa] flex">
      {/* Sidebar */}
      <aside className={`fixed top-0 left-0 h-full bg-[#0f1117] text-white transition-all duration-300 z-50 flex flex-col
        ${sidebarOpen ? 'w-[260px]' : 'w-[72px]'}`}>

        {/* Logo */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-white/[0.07] flex-shrink-0">
          <div className="flex items-center gap-3 min-w-0" aria-label="iVisora Designer Portal">
            {sidebarOpen ? (
              <div className="min-w-0 flex flex-col gap-0.5">
                <IvisoraLogo variant="white" className="h-6 transition-opacity duration-200 hover:opacity-90" />
                <p className="text-[10px] text-white/35 tracking-widest uppercase">Designer Portal</p>
              </div>
            ) : (
              <div
                className="w-9 h-9 rounded-[10px] flex items-center justify-center flex-shrink-0 shadow-lg shadow-violet-900/40"
                style={{ background: 'linear-gradient(135deg,#7B61FF,#C93CFF 55%,#FF4FD8)' }}
                aria-hidden="true"
              >
                <svg viewBox="0 0 24 24" className="w-[18px] h-[18px]" fill="none">
                  <circle cx="12" cy="6.2" r="1.7" fill="white" />
                  <rect x="10.3" y="9.6" width="3.4" height="9.4" rx="1.7" fill="white" />
                </svg>
              </div>
            )}
          </div>
          <button
            onClick={() => setSidebarOpen(o => !o)}
            className="p-1.5 hover:bg-white/10 rounded-lg transition-colors flex-shrink-0 text-white/50 hover:text-white"
          >
            {sidebarOpen ? <ChevronLeft className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          </button>
        </div>

        {/* Nav */}
        <nav className="flex-1 py-3 px-2.5 overflow-y-auto">
          {NAV_GROUPS.map((group, gi) => (
            <div key={gi} className={gi > 0 ? 'pt-4' : ''}>
              {group.label && sidebarOpen && (
                <p className="px-3 pb-1.5 text-[10.5px] font-semibold text-white/30 uppercase tracking-widest select-none">
                  {group.label}
                </p>
              )}
              {group.label && !sidebarOpen && (
                <div className="mx-3 h-px bg-white/[0.06] mb-2" />
              )}

              <div className="space-y-0.5">
                {group.items.map(({ label, path, icon: Icon, action, showBadge }) => {
                  const active = isActive(path);

                  if (action) {
                    return (
                      <button
                        key={path}
                        onClick={() => navigate(path)}
                        title={!sidebarOpen ? label : undefined}
                        className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-[13.5px] font-medium transition-colors
                          bg-gradient-to-r from-violet-600/20 to-fuchsia-600/20 hover:from-violet-600/30 hover:to-fuchsia-600/30
                          text-violet-300 hover:text-violet-100"
                      >
                        <Icon className="w-[18px] h-[18px] flex-shrink-0" />
                        {sidebarOpen && <span>{label}</span>}
                      </button>
                    );
                  }

                  return (
                    <Link
                      key={path}
                      to={path}
                      title={!sidebarOpen ? label : undefined}
                      className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-[13.5px] font-medium transition-all
                        ${active
                          ? 'bg-violet-600 text-white shadow-md shadow-violet-900/20'
                          : 'text-white/50 hover:bg-white/[0.06] hover:text-white/90'
                        }`}
                    >
                      <Icon className="w-[18px] h-[18px] flex-shrink-0" />
                      {sidebarOpen && (
                        <>
                          <span className="flex-1">{label}</span>
                          {showBadge && unreadCount > 0 && (
                            <span className={`text-[11px] font-bold px-1.5 py-0.5 rounded-md leading-none
                              ${active ? 'bg-white/20 text-white' : 'bg-violet-500 text-white'}`}>
                              {unreadCount}
                            </span>
                          )}
                        </>
                      )}
                    </Link>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>

        {/* User footer */}
        <div className="border-t border-white/[0.07] p-3 flex-shrink-0">
          {sidebarOpen ? (
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                {user?.name?.charAt(0)?.toUpperCase() || 'D'}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[13.5px] font-medium text-white truncate">{user?.name || 'Designer'}</p>
                <p className="text-[11px] text-white/40 truncate">{user?.email || ''}</p>
              </div>
              <button onClick={handleLogout} className="p-1.5 text-white/40 hover:text-white hover:bg-white/10 rounded-lg transition-colors" title="Log out">
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <button onClick={handleLogout} className="w-full flex items-center justify-center p-2 text-white/40 hover:text-white hover:bg-white/10 rounded-lg transition-colors" title="Log out">
              <LogOut className="w-5 h-5" />
            </button>
          )}
        </div>
      </aside>

      {/* Main */}
      <div className={`flex-1 flex flex-col min-h-screen transition-all duration-300 ${sidebarOpen ? 'ml-[260px]' : 'ml-[72px]'}`}>
        {/* Topbar */}
        <header className="h-14 bg-white border-b border-gray-200/80 flex items-center justify-between px-6 flex-shrink-0 sticky top-0 z-30 shadow-sm">
          <p className="text-sm font-semibold text-gray-800">{currentLabel}</p>
          <div className="flex items-center gap-3">
            {/* Notification Bell */}
            <div className="relative">
              <button
                onClick={() => setNotifOpen(o => !o)}
                className="relative w-9 h-9 rounded-lg hover:bg-gray-100 flex items-center justify-center transition-colors"
              >
                <Bell className="w-4 h-4 text-gray-500" />
                {unreadCount > 0 && (
                  <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-violet-500 ring-2 ring-white" />
                )}
              </button>
              {notifOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setNotifOpen(false)} />
                  <div className="absolute right-0 top-11 z-50 w-80 bg-white border border-gray-100 rounded-2xl shadow-xl overflow-hidden">
                    <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
                      <p className="font-semibold text-sm text-gray-900">Notifications</p>
                      <span className="text-xs text-gray-400">{unreadCount > 0 ? `${unreadCount} unread` : 'All read'}</span>
                    </div>
                    <div className="max-h-72 overflow-y-auto divide-y divide-gray-50">
                      {notifications.length === 0 ? (
                        <p className="text-sm text-gray-400 text-center py-8">No notifications yet</p>
                      ) : notifications.slice(0, 5).map(n => (
                        <button
                          key={n.id}
                          className="w-full px-4 py-3 hover:bg-gray-50 text-left flex items-start gap-3"
                          onClick={() => { setNotifOpen(false); navigate('/designer/notifications'); }}
                        >
                          {n.type === 'approved'
                            ? <CheckCircle className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                            : <XCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                          }
                          <div className="flex-1 min-w-0">
                            <p className={`text-xs font-semibold text-gray-900 ${!n.is_read ? '' : 'opacity-60'}`}>
                              {n.template_title || (n.type === 'approved' ? 'Template Approved' : 'Template Reviewed')}
                            </p>
                            <p className="text-xs text-gray-500 truncate mt-0.5">{n.message}</p>
                          </div>
                          {!n.is_read && <span className="w-2 h-2 rounded-full bg-violet-500 flex-shrink-0 mt-1" />}
                        </button>
                      ))}
                    </div>
                    <div className="p-3 border-t border-gray-100">
                      <button
                        onClick={() => { setNotifOpen(false); navigate('/designer/notifications'); }}
                        className="w-full text-xs text-violet-600 font-semibold hover:text-violet-700 transition-colors"
                      >
                        View all notifications →
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Avatar */}
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center text-white font-bold text-sm">
              {user?.name?.charAt(0)?.toUpperCase() || 'D'}
            </div>
          </div>
        </header>

        <main className="flex-1 p-6 lg:p-8">{children}</main>
      </div>
    </div>
  );
};

export default DesignerLayout;
